import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import { ASSETS, DONATION_PROJECT } from "@/data/landingData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DONATION_GOAL_FALLBACK = 2500;

const logClientError = (message, error) => {
  if (process.env.NODE_ENV !== "production") {
    console.error(message, error);
  }
};

const normalizeProgress = (progress = {}) => {
  const goal = Number(progress.goal ?? DONATION_GOAL_FALLBACK) || DONATION_GOAL_FALLBACK;
  const raised = Number(progress.raised ?? 0) || 0;
  const remainingRaw = Number(progress.remaining);
  const percentRaw = Number(progress.percent);

  let remaining = Math.max(goal - raised, 0);
  if (Number.isFinite(remainingRaw)) {
    remaining = remainingRaw;
  }

  let percent = 0;
  if (Number.isFinite(percentRaw)) {
    percent = percentRaw;
  } else if (goal) {
    percent = (raised / goal) * 100;
  }

  return {
    goal,
    raised,
    remaining: Math.max(remaining, 0),
    percent: Math.min(percent, 100),
  };
};

const fetchDonationProgress = async () => {
  const response = await axios.get(`${API}/donate/progress`);
  return response.data;
};

const useDonationProgress = () => {
  const [progress, setProgress] = useState(() => normalizeProgress());

  useEffect(() => {
    let isMounted = true;
    const loadProgress = async () => {
      try {
        const data = await fetchDonationProgress();
        if (isMounted && data) {
          setProgress(normalizeProgress(data));
        }
      } catch (error) {
        logClientError("Failed to load donation progress", error);
      }
    };

    loadProgress();
    return () => {
      isMounted = false;
    };
  }, [fetchDonationProgress, logClientError, normalizeProgress]);

  return progress;
};

const DonationHeader = ({ copy }) => (
  <div className="section-header">
    <p className="section-kicker" data-testid="donation-kicker">
      {copy.donation.kicker}
    </p>
    <h2 className="section-title" data-testid="donation-title">
      {copy.donation.title}
    </h2>
    <p className="section-text" data-testid="donation-body">
      {copy.donation.body}
    </p>
  </div>
);

const DonationImpact = ({ copy }) => (
  <div className="donation-impact-grid" data-testid="donation-impact-grid">
    <div className="donation-story" data-testid="donation-story">
      <p className="section-kicker" data-testid="donation-project-subtitle">
        {copy.donationProject.subtitle}
      </p>
      <h3 className="donation-project-title" data-testid="donation-project-title">
        {copy.donationProject.title}
      </h3>
      <p className="section-text" data-testid="donation-project-body">
        {copy.donationProject.body}
      </p>
      <ul className="donation-project-list" data-testid="donation-project-list">
        {copy.donationProject.highlights.map((item, index) => (
          <li key={`${item}-${index}`} data-testid={`donation-project-highlight-${index + 1}`}>
            {item}
          </li>
        ))}
      </ul>
      <p className="donation-project-impact" data-testid="donation-project-impact">
        {copy.donationProject.impact}
      </p>
      <div className="donation-project-location" data-testid="donation-project-location">
        <span className="donation-location-label" data-testid="donation-project-location-label">
          {copy.donationProject.locationLabel}
        </span>
        <a
          className="donation-map-link"
          href={DONATION_PROJECT.mapLink}
          target="_blank"
          rel="noreferrer"
          data-testid="donation-project-map-link"
        >
          {copy.donationProject.mapLabel}
        </a>
      </div>
      <p className="donation-project-cta" data-testid="donation-project-cta">
        {copy.donationProject.ctaLine}
      </p>
    </div>
    <div className="donation-media" data-testid="donation-project-media">
      <img
        src={ASSETS.donationPoster}
        alt={copy.donationProject.imageAlt}
        loading="lazy"
        data-testid="donation-project-image"
      />
    </div>
  </div>
);

const DonationProgressStats = ({ copy, formatter, raised, remaining, goal }) => (
  <div className="donation-progress-stats" data-testid="donation-progress-stats">
    <div className="donation-progress-stat" data-testid="donation-progress-raised">
      <span className="donation-stat-label" data-testid="donation-progress-raised-label">
        {copy.donationTracker.raisedLabel}
      </span>
      <span className="donation-stat-value" data-testid="donation-progress-raised-value">
        {formatter.format(raised)}
      </span>
    </div>
    <div className="donation-progress-stat" data-testid="donation-progress-remaining">
      <span className="donation-stat-label" data-testid="donation-progress-remaining-label">
        {copy.donationTracker.remainingLabel}
      </span>
      <span className="donation-stat-value" data-testid="donation-progress-remaining-value">
        {formatter.format(remaining)}
      </span>
    </div>
    <div className="donation-progress-stat" data-testid="donation-progress-goal">
      <span className="donation-stat-label" data-testid="donation-progress-goal-label">
        {copy.donationTracker.goalLabel}
      </span>
      <span className="donation-stat-value" data-testid="donation-progress-goal-value">
        {formatter.format(goal)}
      </span>
    </div>
  </div>
);

const DonationProgress = ({ copy, progress, formatter }) => {
  const { goal, raised, remaining, percent } = progress;

  return (
    <div className="donation-progress-card" data-testid="donation-progress-card">
      <div>
        <p className="section-kicker" data-testid="donation-progress-kicker">
          {copy.donationTracker.title}
        </p>
        <h3 className="donation-progress-title" data-testid="donation-progress-title">
          {copy.donationTracker.progressLabel}
        </h3>
      </div>
      <div className="donation-progress-bar" data-testid="donation-progress-bar">
        <span
          className="donation-progress-fill"
          style={{ width: `${percent}%` }}
          data-testid="donation-progress-fill"
        />
      </div>
      <p className="donation-progress-percent" data-testid="donation-progress-percent">
        {Math.round(percent)}%
      </p>
      <DonationProgressStats
        copy={copy}
        formatter={formatter}
        raised={raised}
        remaining={remaining}
        goal={goal}
      />
    </div>
  );
};

const DonationButtons = ({ copy }) => (
  <div className="donation-buttons" data-testid="donation-buttons">
    <Link className="donation-button" to="/donate/checkout?amount=5" data-testid="donation-5-link">
      {copy.donation.buttons.five}
    </Link>
    <Link
      className="donation-button recommended"
      to="/donate/checkout?amount=20"
      data-testid="donation-20-link"
    >
      <span className="donation-badge" data-testid="donation-recommended-badge">
        {copy.donation.recommended}
      </span>
      {copy.donation.buttons.twenty}
    </Link>
    <Link className="donation-button" to="/donate/checkout?amount=100" data-testid="donation-100-link">
      {copy.donation.buttons.hundred}
    </Link>
  </div>
);

const DonationNote = ({ copy }) => (
  <p className="donation-note" data-testid="donation-note">
    {copy.donation.note}
  </p>
);

const DonationSection = () => {
  const { copy, language } = useLanguage();
  const progress = useDonationProgress();
  const intl = Intl;

  const formatter = useMemo(
    () =>
      new intl.NumberFormat(language === "fr" ? "fr-FR" : "en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [language, intl],
  );

  return (
    <section className="section donation-section" id="donate" data-testid="donation-section">
      <div className="container donation-container" data-testid="donation-container">
        <DonationHeader copy={copy} />
        <DonationImpact copy={copy} />
        <DonationProgress copy={copy} progress={progress} formatter={formatter} />
        <DonationButtons copy={copy} />
        <DonationNote copy={copy} />
      </div>
    </section>
  );
};

export default DonationSection;